import "@/App.css";
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, AuthCallback, ProtectedRoute } from './contexts/AuthContext';

// Layouts
import { MainLayout } from './components/layouts/MainLayout';
import { PortalLayout } from './components/layouts/PortalLayout';
import { AdminLayout } from './components/layouts/AdminLayout';
import { StaffLayout } from './components/layouts/StaffLayout';

// Public Pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import SignUpPage from './pages/SignUpPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import MembershipSuccessPage from './pages/MembershipSuccessPage';
import MembershipPage from './pages/MembershipPage';
import LocationsPage from './pages/LocationsPage';
import ContactPage from './pages/ContactPage';
import AboutPage from './pages/AboutPage';
import ServicesPage from './pages/ServicesPage';
import FoundersPage from './pages/about/FoundersPage';
import TrainersPage from './pages/TrainersPage';
import ClassesPage from './pages/ClassesPage';
import FAQPage from './pages/FAQPage';
import CareersPage from './pages/CareersPage';
import ShopPage from './pages/ShopPage';
import SuccessStoriesPage from './pages/SuccessStoriesPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import PressPage from './pages/PressPage';

// Portal Pages
import MemberDashboard from './pages/portal/DashboardPage';
import SchedulePage from './pages/portal/SchedulePage';
import MyBookingsPage from './pages/portal/MyBookingsPage';
import ClassesBookingPage from './pages/portal/ClassesBookingPage';
import NutritionPage from './pages/portal/NutritionPage';
import MemberMembershipPage from './pages/portal/MembershipPage';
import SettingsPage from './pages/portal/SettingsPage';
import SupportPage from './pages/portal/SupportPage';
import WorkoutsPage from './pages/portal/WorkoutsPage';

// Staff Pages
import StaffDashboard from './pages/staff/DashboardPage';
import StaffSchedulePage from './pages/staff/SchedulePage';
import StaffClientsPage from './pages/staff/ClientsPage';
import StaffSettingsPage from './pages/staff/SettingsPage';

// Admin Pages
import AdminDashboard from './pages/admin/DashboardPage';
import AdminIntegrationsPage from './pages/admin/IntegrationsPage';
import CMSPagesPage from './pages/admin/CMSPagesPage';
import MediaLibraryPage from './pages/admin/MediaLibraryPage';
import MembersPage from './pages/admin/MembersPage';
import ClassesAdminPage from './pages/admin/ClassesAdminPage';
import LocationsAdminPage from './pages/admin/LocationsAdminPage';
import AnalyticsPage from './pages/admin/AnalyticsPage';
import AdminSettingsPage from './pages/admin/SettingsPage';
import ContactsAdminPage from './pages/admin/ContactsAdminPage';

// Placeholder for any remaining routes
const PlaceholderPage = ({ title }) => (
  <div className="min-h-[60vh] flex items-center justify-center pt-20">
    <div className="text-center">
      <h1 className="text-3xl font-heading font-bold text-gray-900 mb-4">{title}</h1>
      <p className="text-gray-600">This page is coming soon!</p>
    </div>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Auth Routes */}
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignUpPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />

          {/* Public Routes with MainLayout */}
          <Route path="/" element={<MainLayout><HomePage /></MainLayout>} />
          <Route path="/about" element={<MainLayout><AboutPage /></MainLayout>} />
          <Route path="/about/founders" element={<MainLayout><FoundersPage /></MainLayout>} />
          <Route path="/services" element={<MainLayout><ServicesPage /></MainLayout>} />
          <Route path="/classes" element={<MainLayout><ClassesPage /></MainLayout>} />
          <Route path="/membership" element={<MainLayout><MembershipPage /></MainLayout>} />
          <Route path="/membership/success" element={<MembershipSuccessPage />} />
          <Route path="/locations" element={<MainLayout><LocationsPage /></MainLayout>} />
          <Route path="/locations/:id" element={<MainLayout><LocationsPage /></MainLayout>} />
          <Route path="/contact" element={<MainLayout><ContactPage /></MainLayout>} />
          <Route path="/trainers" element={<MainLayout><TrainersPage /></MainLayout>} />
          <Route path="/trainers/:id" element={<MainLayout><TrainersPage /></MainLayout>} />
          <Route path="/faq" element={<MainLayout><FAQPage /></MainLayout>} />
          <Route path="/careers" element={<MainLayout><CareersPage /></MainLayout>} />
          <Route path="/shop" element={<MainLayout><ShopPage /></MainLayout>} />
          <Route path="/success-stories" element={<MainLayout><SuccessStoriesPage /></MainLayout>} />
          <Route path="/privacy" element={<MainLayout><PrivacyPage /></MainLayout>} />
          <Route path="/terms" element={<MainLayout><TermsPage /></MainLayout>} />

          {/* Member Portal Routes */}
          <Route path="/portal" element={
            <ProtectedRoute>
              <PortalLayout><MemberDashboard /></PortalLayout>
            </ProtectedRoute>
          } />
          <Route path="/portal/schedule" element={
            <ProtectedRoute>
              <PortalLayout><SchedulePage /></PortalLayout>
            </ProtectedRoute>
          } />
          <Route path="/portal/bookings" element={
            <ProtectedRoute>
              <PortalLayout><MyBookingsPage /></PortalLayout>
            </ProtectedRoute>
          } />
          <Route path="/portal/classes" element={
            <ProtectedRoute>
              <PortalLayout><ClassesBookingPage /></PortalLayout>
            </ProtectedRoute>
          } />
          <Route path="/portal/nutrition" element={
            <ProtectedRoute>
              <PortalLayout><NutritionPage /></PortalLayout>
            </ProtectedRoute>
          } />
          <Route path="/portal/membership" element={
            <ProtectedRoute>
              <PortalLayout><MemberMembershipPage /></PortalLayout>
            </ProtectedRoute>
          } />
          <Route path="/portal/settings" element={
            <ProtectedRoute>
              <PortalLayout><SettingsPage /></PortalLayout>
            </ProtectedRoute>
          } />
          <Route path="/portal/support" element={
            <ProtectedRoute>
              <PortalLayout><SupportPage /></PortalLayout>
            </ProtectedRoute>
          } />
          <Route path="/portal/workouts" element={
            <ProtectedRoute>
              <PortalLayout><WorkoutsPage /></PortalLayout>
            </ProtectedRoute>
          } />

          {/* Admin Routes */}
          <Route path="/admin" element={
            <ProtectedRoute allowedRoles={['admin', 'founder', 'manager', 'super_admin']}>
              <AdminLayout><AdminDashboard /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/integrations" element={
            <ProtectedRoute allowedRoles={['admin', 'founder', 'super_admin']}>
              <AdminLayout><AdminIntegrationsPage /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/users" element={
            <ProtectedRoute allowedRoles={['admin', 'founder', 'manager', 'super_admin']}>
              <AdminLayout><MembersPage /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/members" element={
            <ProtectedRoute allowedRoles={['admin', 'founder', 'manager', 'super_admin']}>
              <AdminLayout><MembersPage /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/cms/pages" element={
            <ProtectedRoute allowedRoles={['admin', 'founder', 'manager', 'super_admin', 'cms_admin', 'staff']}>
              <AdminLayout><CMSPagesPage /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/cms/media" element={
            <ProtectedRoute allowedRoles={['admin', 'founder', 'manager', 'super_admin', 'cms_admin', 'staff']}>
              <AdminLayout><MediaLibraryPage /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/classes" element={
            <ProtectedRoute allowedRoles={['admin', 'founder', 'manager', 'super_admin', 'cms_admin', 'staff']}>
              <AdminLayout><ClassesAdminPage /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/locations" element={
            <ProtectedRoute allowedRoles={['admin', 'founder', 'manager', 'super_admin']}>
              <AdminLayout><LocationsAdminPage /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/calendar" element={
            <ProtectedRoute allowedRoles={['admin', 'founder', 'manager', 'super_admin', 'cms_admin', 'staff']}>
              <AdminLayout><ClassesAdminPage /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/analytics" element={
            <ProtectedRoute allowedRoles={['admin', 'founder', 'super_admin']}>
              <AdminLayout><AnalyticsPage /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/contacts" element={
            <ProtectedRoute allowedRoles={['admin', 'founder', 'manager', 'super_admin']}>
              <AdminLayout><ContactsAdminPage /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/settings" element={
            <ProtectedRoute allowedRoles={['admin', 'founder', 'super_admin']}>
              <AdminLayout><AdminSettingsPage /></AdminLayout>
            </ProtectedRoute>
          } />

          {/* Staff Portal Routes */}
          <Route path="/staff" element={
            <ProtectedRoute allowedRoles={['trainer', 'staff', 'admin', 'founder', 'manager', 'super_admin', 'cms_admin']}>
              <StaffLayout><StaffDashboard /></StaffLayout>
            </ProtectedRoute>
          } />
          <Route path="/staff/schedule" element={
            <ProtectedRoute allowedRoles={['trainer', 'staff', 'admin', 'founder', 'manager']}>
              <StaffLayout><StaffSchedulePage /></StaffLayout>
            </ProtectedRoute>
          } />
          <Route path="/staff/clients" element={
            <ProtectedRoute allowedRoles={['trainer', 'staff', 'admin', 'founder', 'manager']}>
              <StaffLayout><StaffClientsPage /></StaffLayout>
            </ProtectedRoute>
          } />
          <Route path="/staff/classes" element={
            <ProtectedRoute allowedRoles={['trainer', 'staff', 'admin', 'founder', 'manager']}>
              <StaffLayout><StaffSchedulePage /></StaffLayout>
            </ProtectedRoute>
          } />
          <Route path="/staff/settings" element={
            <ProtectedRoute allowedRoles={['trainer', 'staff', 'admin', 'founder', 'manager']}>
              <StaffLayout><StaffSettingsPage /></StaffLayout>
            </ProtectedRoute>
          } />

          {/* Additional Public Pages */}
          <Route path="/press" element={<MainLayout><PressPage /></MainLayout>} />
          <Route path="/careers/:id" element={<MainLayout><CareersPage /></MainLayout>} />
          <Route path="/classes/:id" element={<MainLayout><ClassesPage /></MainLayout>} />
          <Route path="/success-stories/:id" element={<MainLayout><SuccessStoriesPage /></MainLayout>} />

          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
